"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractedExpenseSchema = exports.expenseBankImportDecisionsSchema = exports.expenseBankImportDecisionSchema = void 0;
const zod_1 = require("zod");
const enums_1 = require("./enums");
const EXPENSE_KIND_VALUES = Object.values(enums_1.ExpenseKind);
/**
 * One user decision for a non-`alreadyImported` debit row, keyed by dedupHash:
 *  - 'match' allocates the transaction to that expense (validated server-side
 *    against the row's own candidates — the client is never trusted);
 *  - 'create' stores the transaction AND creates a new expense from it
 *    (default kind OTHER — bank fees, taxes, salaries), fully allocated;
 *  - 'ignore' stores the transaction as IGNORED (visible, but out of the
 *    reconciliation workspace).
 * A row with NO decision is stored as UNMATCHED for later reconciliation.
 */
exports.expenseBankImportDecisionSchema = zod_1.z.discriminatedUnion('action', [
    zod_1.z.object({
        action: zod_1.z.literal('match'),
        expenseId: zod_1.z.uuid('expenseId invalid.'),
    }),
    zod_1.z.object({
        action: zod_1.z.literal('create'),
        kind: zod_1.z.enum(EXPENSE_KIND_VALUES, 'Tip de cheltuială invalid.').default('OTHER'),
        description: zod_1.z.string().trim().max(500).optional(),
        categoryId: zod_1.z.uuid('categoryId invalid.').optional(),
        /** Free-text category (find-or-create) — used by the quick-create
         * heuristics before the category exists. */
        categoryName: zod_1.z.string().trim().min(1).max(120).optional(),
        supplierName: zod_1.z.string().trim().min(1).optional(),
    }),
    zod_1.z.object({ action: zod_1.z.literal('ignore') }),
]);
/** Loose record, same reasoning as `bankImportDecisionsSchema`: one malformed
 * entry must become one `failed` row, not reject the whole request. */
exports.expenseBankImportDecisionsSchema = zod_1.z.record(zod_1.z.string(), zod_1.z.unknown());
// --- AI extraction (Gemini) ------------------------------------------------
/**
 * What the vision model is asked to return for an uploaded invoice/receipt.
 * Everything is optional/nullable — a blurry photo may yield only a total —
 * and the SERVICE re-validates and normalizes (2dp amounts, ISO date, CIF
 * without RO) before handing it to the UI; the UI treats it all as a
 * pre-filled draft the user must confirm.
 */
exports.extractedExpenseSchema = zod_1.z.object({
    supplierName: zod_1.z.string().trim().min(1).nullish(),
    supplierCif: zod_1.z.string().trim().min(2).max(24).nullish(),
    /** ISO 3166-1 alpha-2, best effort. */
    supplierCountry: zod_1.z.string().trim().length(2).nullish(),
    docNumber: zod_1.z.string().trim().min(1).max(64).nullish(),
    docDate: zod_1.z.string().trim().nullish(),
    /** Loose on purpose: the model may echo "Lei." off a receipt — the API's
     * normalize step keeps a clean ISO code and drops anything else. */
    currency: zod_1.z.string().trim().max(16).nullish(),
    netAmount: zod_1.z.string().trim().nullish(),
    vatAmount: zod_1.z.string().trim().nullish(),
    totalAmount: zod_1.z.string().trim().nullish(),
    kind: zod_1.z.enum(['INVOICE', 'RECEIPT']).nullish(),
});
